import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Loader2 } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader } from "./shared";

export default function CampaignPerformanceSummary() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    execService.campaignSummary(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div data-testid="exec-campaign-summary-page">
      <PageHeader icon={BarChart3} title="Campaign Performance Summary" subtitle="Per-campaign reach and engagement across channels.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="campaign-empty"><p className="text-sm text-muted-foreground">No campaigns yet.</p></div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="campaign-table">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">
              <th className="px-4 py-2">Campaign</th><th className="px-4 py-2">Channel</th><th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Sent</th><th className="px-4 py-2 text-right">Delivered</th>
              <th className="px-4 py-2 text-right">Read</th><th className="px-4 py-2 text-right">Replied</th>
              <th className="px-4 py-2 text-right">Delivery</th><th className="px-4 py-2 text-right">Reply</th>
            </tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-2 text-foreground font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-muted-foreground capitalize">{c.channel || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.status}</td>
                  <td className="px-4 py-2 text-right text-foreground">{c.sent.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-foreground">{c.delivered.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-foreground">{c.read.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right text-foreground">{c.replied.toLocaleString()}</td>
                  <td className="px-4 py-2 text-right font-semibold text-emerald-600">{c.deliveryRate}%</td>
                  <td className="px-4 py-2 text-right font-semibold text-sky-600">{c.replyRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
